import React, { useCallback, useEffect, useState } from "react";
import {
  Calendar, Copy, Check, ArrowsClockwise, Eye, EyeSlash, Trash, AppleLogo, GoogleLogo, MicrosoftOutlookLogo,
  CalendarCheck, Pill, ArrowClockwise, WarningCircle,
} from "@phosphor-icons/react";
import { getCalendarToken, rotateCalendarToken, revokeCalendarToken, getCalendarFeedUrl, getCalendarPreview } from "@/lib/api";

export default function CalendarSyncView() {
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCalendarToken();
      setTokenData(data);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);
  const loadPreview = useCallback(async () => {
    setPreviewLoading(true); setPreviewErr("");
    try {
      const data = await getCalendarPreview(5);
      setPreview(data);
    } catch (e) { setPreviewErr(e?.message || "Failed to load preview"); }
    finally { setPreviewLoading(false); }
  }, []);
  useEffect(() => { load(); loadPreview(); }, [load, loadPreview]);

  const feedUrl = tokenData ? getCalendarFeedUrl(tokenData.token) : "";
  const webcalUrl = feedUrl ? feedUrl.replace(/^https?:/, "webcal:") : "";

  function copy(text, label) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(""), 1800);
    });
  }

  async function handleRotate() {
    if (!window.confirm("Rotating will invalidate the old feed URL — any calendar app subscribed to the old URL will stop syncing until you re-subscribe with the new one. Continue?")) return;
    setBusy(true); setErr("");
    try {
      const data = await rotateCalendarToken();
      setTokenData((p) => ({ ...p, token: data.token, createdAt: data.createdAt, accessCount: 0, lastAccessedAt: "" }));
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleRevoke() {
    if (!window.confirm("Revoke will delete the calendar feed entirely. Subscribers will stop syncing immediately. You can create a new feed at any time. Continue?")) return;
    setBusy(true); setErr("");
    try {
      await revokeCalendarToken();
      setTokenData(null);
      setShowToken(false);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="view-grid" data-testid="calendar-sync-view">
      <div className="panel master-pdf-panel">
        <div className="master-pdf-inner">
          <div className="master-pdf-left">
            <div className="master-pdf-tag"><Calendar size={12} weight="fill" /> CALENDAR SYNC</div>
            <h2 className="master-pdf-title">Sync appointments + Rx refills to your calendar.</h2>
            <p className="master-pdf-desc">
              ClaimOps publishes a private read-only calendar feed (iCalendar / .ics format).
              Subscribe to it from Google Calendar, Apple Calendar, or Outlook and your
              ClaimOps appointments and prescription refills will appear automatically — with reminders.
              Works on any device that supports iCal. No Google sign-in required.
            </p>
            <ul className="master-pdf-list">
              <li>Appointments → calendar events with provider, location, linked condition, and your custom reminder.</li>
              <li>Active prescriptions → all-day refill events with a 3-day-before alarm.</li>
              <li>Read-only on the calendar side. Edit in ClaimOps; changes appear next time your calendar app polls.</li>
              <li>Rotate or revoke the feed at any time.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="panel" data-testid="calendar-feed-panel">
        <div className="panel-header">
          <Calendar size={16} weight="bold" />
          <span>YOUR CALENDAR FEED URL</span>
        </div>
        <div className="panel-body">
          {loading ? (
            <div style={{ color: "var(--text-secondary)" }}>Loading...</div>
          ) : !tokenData ? (
            <div>
              <p>You don't have an active feed yet. Click below to create one.</p>
              <button className="btn btn--accent" onClick={load} data-testid="calendar-create-btn">
                <Calendar size={14} weight="bold" /> CREATE FEED
              </button>
            </div>
          ) : (
            <>
              <div style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 4, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-secondary)", marginBottom: 6 }}>
                  HTTPS FEED URL (paste into Google Calendar / Outlook)
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    readOnly
                    value={showToken ? feedUrl : feedUrl.replace(tokenData.token, "•".repeat(Math.max(8, tokenData.token.length)))}
                    className="field-input mono"
                    style={{ flex: 1, minWidth: 280, fontSize: 12 }}
                    data-testid="calendar-feed-input"
                    onFocus={(e) => e.target.select()}
                  />
                  <button className="btn btn--outline btn--sm" onClick={() => setShowToken((p) => !p)} title={showToken ? "Hide" : "Show"} data-testid="calendar-toggle-show">
                    {showToken ? <EyeSlash size={12} weight="bold" /> : <Eye size={12} weight="bold" />}
                  </button>
                  <button className="btn btn--accent btn--sm" onClick={() => copy(feedUrl, "https")} data-testid="calendar-copy-https">
                    {copied === "https" ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />} {copied === "https" ? "COPIED" : "COPY"}
                  </button>
                </div>
              </div>

              <div style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 4, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-secondary)", marginBottom: 6 }}>
                  WEBCAL:// URL (one-click for Apple Calendar / iOS / macOS)
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    readOnly
                    value={showToken ? webcalUrl : webcalUrl.replace(tokenData.token, "•".repeat(Math.max(8, tokenData.token.length)))}
                    className="field-input mono"
                    style={{ flex: 1, minWidth: 280, fontSize: 12 }}
                    data-testid="calendar-webcal-input"
                    onFocus={(e) => e.target.select()}
                  />
                  <a href={webcalUrl} className="btn btn--accent btn--sm" data-testid="calendar-subscribe-apple">
                    <AppleLogo size={12} weight="bold" /> SUBSCRIBE
                  </a>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                <div style={{ background: "rgba(31, 58, 107, 0.06)", border: "1px solid rgba(31, 58, 107, 0.15)", borderRadius: 4, padding: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <GoogleLogo size={14} weight="bold" />
                    <strong style={{ fontSize: 13 }}>Google Calendar</strong>
                  </div>
                  <ol style={{ fontSize: 12, paddingLeft: 18, color: "var(--text-primary)", margin: 0, lineHeight: 1.55 }}>
                    <li>Open <a href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl" target="_blank" rel="noreferrer">Google Calendar Settings → Add by URL</a>.</li>
                    <li>Paste the HTTPS feed URL above.</li>
                    <li>Click <strong>Add Calendar</strong>. ClaimOps appears as a separate calendar (toggle/color it as you like).</li>
                    <li>Google polls every 12-24h. To refresh sooner, edit any event title in ClaimOps and re-save.</li>
                  </ol>
                </div>
                <div style={{ background: "rgba(31, 58, 107, 0.06)", border: "1px solid rgba(31, 58, 107, 0.15)", borderRadius: 4, padding: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <AppleLogo size={14} weight="bold" />
                    <strong style={{ fontSize: 13 }}>Apple Calendar / iOS</strong>
                  </div>
                  <ol style={{ fontSize: 12, paddingLeft: 18, color: "var(--text-primary)", margin: 0, lineHeight: 1.55 }}>
                    <li>Click the <strong>SUBSCRIBE</strong> button above (uses webcal://).</li>
                    <li>Or: Calendar → File → New Calendar Subscription → paste webcal:// URL.</li>
                    <li>Choose how often to refresh (5 min / 15 min / hourly).</li>
                  </ol>
                </div>
                <div style={{ background: "rgba(31, 58, 107, 0.06)", border: "1px solid rgba(31, 58, 107, 0.15)", borderRadius: 4, padding: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <MicrosoftOutlookLogo size={14} weight="bold" />
                    <strong style={{ fontSize: 13 }}>Outlook (Web + Desktop)</strong>
                  </div>
                  <ol style={{ fontSize: 12, paddingLeft: 18, color: "var(--text-primary)", margin: 0, lineHeight: 1.55 }}>
                    <li>Outlook on the web: Calendar → Add calendar → Subscribe from web → paste HTTPS URL.</li>
                    <li>Outlook desktop: Open Calendar → Home → Open Calendar → From Internet → paste URL.</li>
                  </ol>
                </div>
                <div style={{ background: "var(--gold-100, rgba(201, 162, 74, 0.14))", border: "1px solid var(--gold-500, #C9A24A)", borderRadius: 4, padding: 12 }}>
                  <strong style={{ fontSize: 13, color: "var(--text-primary)" }}>Privacy</strong>
                  <p style={{ fontSize: 12, color: "var(--text-primary)", margin: "6px 0 0" }}>
                    Anyone with this URL can view your appointment titles, providers, locations, and Rx refill schedule (read-only).
                    Treat it like a password. <strong>Rotate</strong> if shared by accident; <strong>revoke</strong> to stop all sync.
                  </p>
                </div>
              </div>

              <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 14 }}>
                Created: <span className="mono">{tokenData.createdAt ? new Date(tokenData.createdAt).toLocaleString() : "—"}</span>{" "}
                · Last polled: <span className="mono">{tokenData.lastAccessedAt ? new Date(tokenData.lastAccessedAt).toLocaleString() : "never"}</span>{" "}
                · Access count: <span className="mono">{tokenData.accessCount ?? 0}</span>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn btn--outline" onClick={handleRotate} disabled={busy} data-testid="calendar-rotate-btn">
                  <ArrowsClockwise size={14} weight="bold" /> ROTATE FEED URL
                </button>
                <button className="btn btn--danger" onClick={handleRevoke} disabled={busy} data-testid="calendar-revoke-btn">
                  <Trash size={14} weight="bold" /> REVOKE FEED
                </button>
              </div>
            </>
          )}
          {err && <div className="form-report form-report--err" data-testid="calendar-err" style={{ marginTop: 12 }}>{err}</div>}
        </div>
      </div>

      <div className="panel" data-testid="calendar-preview-panel">
        <div className="panel-header">
          <CalendarCheck size={16} weight="bold" />
          <span>TEST YOUR FEED — NEXT 5 EVENTS</span>
          <button
            className="btn btn--outline btn--sm"
            onClick={loadPreview}
            disabled={previewLoading}
            style={{ marginLeft: "auto" }}
            data-testid="calendar-preview-refresh"
            title="Refresh preview"
          >
            <ArrowClockwise size={12} weight="bold" /> {previewLoading ? "LOADING..." : "REFRESH"}
          </button>
        </div>
        <div className="panel-body">
          <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 0, marginBottom: 12 }}>
            This is exactly what subscribers will see once their calendar app polls your feed. If something is missing,
            add the appointment / prescription in ClaimOps first, then refresh — no need to re-subscribe.
          </p>
          {previewErr && (
            <div className="form-report form-report--err" data-testid="calendar-preview-err">
              <WarningCircle size={12} weight="bold" /> {previewErr}
            </div>
          )}
          {!previewErr && preview && (
            <>
              <div className="calendar-preview-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginBottom: 12 }}>
                <div style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 4, padding: "8px 10px" }} data-testid="calendar-preview-kpi-upcoming">
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-secondary)" }}>UPCOMING</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text-primary)" }}>{preview.totalUpcoming}</div>
                </div>
                <div style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 4, padding: "8px 10px" }} data-testid="calendar-preview-kpi-appts">
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-secondary)" }}>APPOINTMENTS</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text-primary)" }}>{preview.totalAppointments}</div>
                </div>
                <div style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 4, padding: "8px 10px" }} data-testid="calendar-preview-kpi-rx">
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-secondary)" }}>ACTIVE RX</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text-primary)" }}>{preview.totalPrescriptions}</div>
                </div>
              </div>
              {preview.events.length === 0 ? (
                <div
                  data-testid="calendar-preview-empty"
                  style={{ background: "var(--gold-100, rgba(201, 162, 74, 0.14))", border: "1px solid var(--gold-500, #C9A24A)", borderRadius: 4, padding: 14, fontSize: 12, color: "var(--text-primary)" }}
                >
                  <strong>No upcoming events.</strong> Your feed is valid but currently empty. Add an upcoming appointment
                  or refill a prescription, then click REFRESH. Subscribers will see new events on their calendar app's next poll.
                </div>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }} data-testid="calendar-preview-list">
                  {preview.events.map((ev) => {
                    const start = new Date(ev.start);
                    const dateStr = ev.allDay
                      ? start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })
                      : start.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
                    const isAppt = ev.kind === "appointment";
                    return (
                      <li
                        key={`${ev.kind}-${ev.id}`}
                        data-testid={`calendar-preview-event-${ev.id}`}
                        style={{
                          display: "flex", gap: 10, padding: 10,
                          borderRadius: 4, border: "1px solid var(--border)",
                          background: isAppt ? "var(--surface-2)" : "var(--surface-2)",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 4, background: isAppt ? "var(--navy-700, #1F3A6B)" : "var(--success, #10B981)", color: "#fff", flexShrink: 0 }}>
                          {isAppt ? <CalendarCheck size={14} weight="bold" /> : <Pill size={14} weight="bold" />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {ev.summary}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2, display: "flex", flexWrap: "wrap", gap: 10 }}>
                            <span className="mono">{dateStr}{ev.allDay ? " (all-day)" : ""}</span>
                            {ev.provider && <span>Provider: {ev.provider}</span>}
                            {ev.location && <span>Location: {ev.location}</span>}
                            {ev.linkedConditions?.length > 0 && <span>Linked: {ev.linkedConditions.join(", ")}</span>}
                            <span>Alarm: {ev.alarmDaysBefore}d before</span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 10 }}>
                Generated: <span className="mono">{preview.generatedAt ? new Date(preview.generatedAt).toLocaleString() : "—"}</span>{" "}
                · Showing up to 5 of {preview.totalUpcoming} upcoming events.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
